import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Newspaper, Clock, ArrowRight } from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";
import { KB_CATEGORIES, ARTICLES, catBySlug } from "@/data/knowledge";
import { KbIcon } from "@/components/content/kbIcons";
import { cmsService } from "@/services/cms";

const normalize = (a) => ({
  slug: a.slug,
  title: a.title,
  category: a.category || "buying",
  excerpt: a.excerpt || "",
  readTime: a.read_time || a.readTime || 4,
  date: a.date || a.created_at || new Date().toISOString(),
});

const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export const LatestNews = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      let cms = [];
      try { cms = (await cmsService.articles()) || []; } catch { cms = []; }
      const merged = [...cms.map(normalize)];
      const seen = new Set(merged.map((a) => a.slug));
      for (const a of ARTICLES) { if (!seen.has(a.slug)) merged.push(normalize(a)); }
      merged.sort((a, b) => new Date(b.date) - new Date(a.date));
      if (alive) setItems(merged.slice(0, 3));
    })();
    return () => { alive = false; };
  }, []);

  const list = items || [];

  return (
    <section className="bg-background py-12 sm:py-16" data-testid="latest-news-section">
      <div className="abay-container">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#F59E0B]/12 px-3 py-1 text-xs font-semibold text-[#D97706]">
            <Newspaper className="h-3.5 w-3.5" /> Market pulse
          </span>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Latest Real Estate News</h2>
          <p className="mt-3 text-muted-foreground">Guides, market trends and tips to help you buy, rent and invest smarter.</p>
        </div>
        <Link to="/blog" data-testid="latest-news-view-all" className="flex shrink-0 items-center gap-1 text-sm font-semibold text-[#1E3A8A] hover:underline dark:text-[#7AA0FF]">
          View all news <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {items === null ? (
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-64 animate-pulse rounded-2xl border border-border bg-muted/50" />
          ))}
        </div>
      ) : (
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3"
        >
          {list.map((a) => {
            const cat = catBySlug(a.category) || KB_CATEGORIES[0];
            return (
              <motion.div key={a.slug} variants={fadeUp}>
                <Link
                  to={`/blog/${a.slug}`}
                  data-testid={`news-card-${a.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-sm)] transition-transform duration-200 hover:-translate-y-1 hover:shadow-[var(--shadow-md)]"
                >
                  <div className="relative flex h-36 items-center justify-center" style={{ background: `linear-gradient(135deg, ${cat.color}, ${cat.color}CC)` }}>
                    <KbIcon name={cat.icon} className="h-12 w-12 text-white/90" />
                    <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium text-slate-800">{cat.label}</span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="text-lg font-semibold leading-snug text-foreground transition-colors group-hover:text-[#1E3A8A] dark:group-hover:text-[#7AA0FF]">{a.title}</h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{a.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between pt-2 text-xs text-muted-foreground">
                      <span>{fmtDate(a.date)}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {a.readTime} min read</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      )}
      </div>
    </section>
  );
};
